/// <reference path="../../../types/axios-fix.d.ts" />
import { BaseService, httpClientService, authInterceptorService, errorHandlerService, cacheService } from '../core';

/**
 * Role interface
 */
export interface Role {
  _id: string;
  id?: string;
  name: string;
  description?: string;
  permissions: string[];
  level: 'system' | 'organization' | 'team';
  organizationId?: string;
  teamId?: string;
  isSystem?: boolean;
  isDefault?: boolean;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

/**
 * Member interface (for role assignment results)
 */
export interface Member {
  id: string;
  userId: string;
  organizationId?: string;
  teamId?: string;
  roleId: string;
  roleName?: string;
  permissions?: string[];
  joinedAt?: Date | string;
}

/**
 * Create Role Request
 */
export interface CreateRoleRequest {
  name: string;
  description?: string;
  permissions: string[];
  organizationId?: string;
  teamId?: string;
  level: 'system' | 'organization' | 'team';
}

/**
 * Update Role Request
 */
export interface UpdateRoleRequest {
  name?: string;
  description?: string;
  permissions?: string[];
}

/**
 * Role Query Parameters
 */
export interface RoleQueryParams {
  organizationId?: string;
  teamId?: string;
  level?: 'system' | 'organization' | 'team';
  includePermissions?: boolean;
}

/**
 * Role Assignment Scope
 */
export interface RoleAssignmentScope {
  organizationId?: string;
  teamId?: string;
}

/**
 * Role Service
 * 
 * Handles all role-related operations including:
 * - Role CRUD operations
 * - Role assignment and removal
 * - Permission management
 * - Role queries and filtering
 * 
 * Extracted from the monolithic API service.
 */
export class RoleService extends BaseService {
  constructor() {
    super(httpClientService, authInterceptorService, errorHandlerService, cacheService);
  }

  // ===== ROLE CRUD OPERATIONS =====

  /**
   * Get roles with optional filtering
   */
  async getRoles(params?: RoleQueryParams): Promise<Role[]> {
    try {
      console.log('Fetching roles with params:', params);
      
      // Transform client params to match gateway validation schema
      const apiParams: any = {};
      
      if (params) {
        // Map organizationId to scope_entity_id
        if (params.organizationId) {
          apiParams.scope_entity_id = params.organizationId;
          apiParams.scope = 'ORGANIZATION';
        }
        
        // Map teamId to scope_entity_id
        if (params.teamId) {
          apiParams.scope_entity_id = params.teamId;
          apiParams.scope = 'TEAM';
        }
        
        // Map level to type (keeping scope if not already set)
        if (params.level) {
          apiParams.type = params.level.toUpperCase();
          if (!apiParams.scope && params.level !== 'system') {
            apiParams.scope = params.level.toUpperCase();
          }
        }
        
        // Pass through other params
        if (params.includePermissions) {
          apiParams.include_permissions = params.includePermissions;
        }
      }
      
      console.log('Transformed API params:', apiParams);
      
      const response = await this.getCached<any>('/rbac/roles', apiParams);
      
      // Handle both array and wrapped response formats
      let roles: any[] = [];
      if (Array.isArray(response)) {
        roles = response;
      } else if (response?.roles && Array.isArray(response.roles)) {
        roles = response.roles;
      } else if (response?.data && Array.isArray(response.data)) {
        roles = response.data;
      }
      
      return roles.map(this.normalizeRole);
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw this.errorHandler.handleError(error, 'RoleService.getRoles');
    }
  }

  /**
   * Get role by ID
   */
  async getRoleById(roleId: string): Promise<Role> {
    try {
      console.log(`Fetching role: ${roleId}`);
      
      const response = await this.getCached<any>(`/rbac/roles/${roleId}`);
      return this.normalizeRole(response);
    } catch (error) {
      console.error(`Error fetching role ${roleId}:`, error);
      throw this.errorHandler.handleError(error, `RoleService.getRoleById(${roleId})`);
    }
  }

  /**
   * Create a new role
   */
  async createRole(data: CreateRoleRequest): Promise<Role> {
    try {
      console.log('Creating role:', data.name);
      
      const response = await this.post<any>('/rbac/roles', data);
      
      // Invalidate cache
      this.invalidateCache('rbac');
      
      return this.normalizeRole(response);
    } catch (error) {
      console.error('Error creating role:', error);
      throw this.errorHandler.handleError(error, 'RoleService.createRole');
    }
  }

  /**
   * Update an existing role
   */
  async updateRole(roleId: string, data: UpdateRoleRequest): Promise<Role> {
    try {
      console.log(`Updating role ${roleId}:`, data);
      
      const response = await this.put<any>(`/rbac/roles/${roleId}`, data);
      
      // Invalidate cache
      this.invalidateCache('rbac');
      
      return this.normalizeRole(response);
    } catch (error) {
      console.error(`Error updating role ${roleId}:`, error);
      throw this.errorHandler.handleError(error, `RoleService.updateRole(${roleId})`);
    }
  }

  /**
   * Delete a role
   */
  async deleteRole(roleId: string): Promise<void> {
    try {
      console.log(`Deleting role: ${roleId}`);
      
      await this.delete(`/rbac/roles/${roleId}`);
      
      // Invalidate cache
      this.invalidateCache('rbac');
    } catch (error) {
      console.error(`Error deleting role ${roleId}:`, error);
      throw this.errorHandler.handleError(error, `RoleService.deleteRole(${roleId})`);
    }
  }

  // ===== ROLE ASSIGNMENT OPERATIONS =====

  /**
   * Assign a role to a user
   */
  async assignRoleToUser(
    userId: string,
    roleId: string,
    scope: RoleAssignmentScope
  ): Promise<Member> {
    try {
      console.log(`Assigning role ${roleId} to user ${userId}`, scope);
      
      const response = await this.post<any>(
        `/users/${userId}/roles/${roleId}`,
        scope
      );
      
      // Invalidate caches
      this.invalidateCache('rbac');
      this.invalidateCache('user');
      
      return this.normalizeMember(response);
    } catch (error) {
      console.error(`Error assigning role ${roleId} to user ${userId}:`, error);
      throw this.errorHandler.handleError(error, `RoleService.assignRoleToUser(${userId}, ${roleId})`);
    }
  }

  /**
   * Remove a role from a user
   */
  async removeRoleFromUser(
    userId: string,
    roleId: string,
    scope: RoleAssignmentScope
  ): Promise<Member> {
    try {
      console.log(`Removing role ${roleId} from user ${userId}`, scope);
      
      const params = new URLSearchParams(scope as any).toString();
      const response = await this.delete<any>(
        `/users/${userId}/roles/${roleId}${params ? `?${params}` : ''}`
      );
      
      // Invalidate caches
      this.invalidateCache('rbac');
      this.invalidateCache('user');
      
      return this.normalizeMember(response);
    } catch (error) {
      console.error(`Error removing role ${roleId} from user ${userId}:`, error);
      throw this.errorHandler.handleError(error, `RoleService.removeRoleFromUser(${userId}, ${roleId})`);
    }
  }

  // ===== SPECIALIZED ROLE OPERATIONS =====

  /**
   * Get team roles
   */
  async getTeamRoles(teamId: string, includeSystemRoles: boolean = true): Promise<Role[]> {
    try {
      console.log(`Fetching roles for team ${teamId}`);
      
      const response = await this.getCached<any>(
        `/teams/${teamId}/roles`,
        { include_system_roles: includeSystemRoles }
      );
      
      const roles = response.roles || response || [];
      return roles.map(this.normalizeRole);
    } catch (error) {
      console.error(`Error fetching roles for team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `RoleService.getTeamRoles(${teamId})`);
    }
  }

  /**
   * Get organization roles
   */
  async getOrganizationRoles(
    organizationId: string,
    includeSystemRoles: boolean = true
  ): Promise<Role[]> {
    try {
      console.log(`Fetching roles for organization ${organizationId}`);
      
      return this.getRoles({
        organizationId,
        level: 'organization',
        includePermissions: true
      });
    } catch (error) {
      console.error(`Error fetching roles for organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `RoleService.getOrganizationRoles(${organizationId})`);
    }
  }

  // ===== HELPER METHODS =====

  /**
   * Normalize role data from API response
   */
  private normalizeRole(roleData: any): Role {
    // Determine if this is a system role (isSystemRole has priority)
    const isSystem = roleData.isSystemRole !== undefined ? roleData.isSystemRole :
                     (roleData.isSystem !== undefined ? roleData.isSystem : roleData.is_system);

    // Map API type/scope to level
    // System roles should have level='system' regardless of their type field
    let level: 'system' | 'organization' | 'team' = 'organization';

    if (isSystem) {
      // System roles always get level='system'
      level = 'system';
    } else if (roleData.level) {
      // Use existing level if provided
      level = roleData.level;
    } else if (roleData.type) {
      // Fall back to type mapping for custom roles
      const typeUpper = roleData.type.toUpperCase();
      if (typeUpper === 'SYSTEM') level = 'system';
      else if (typeUpper === 'ORGANIZATION') level = 'organization';
      else if (typeUpper === 'TEAM') level = 'team';
    }

    // Map scope entity ID to appropriate org/team ID
    let organizationId = roleData.organizationId || roleData.organization_id;
    let teamId = roleData.teamId || roleData.team_id;

    // If scopeEntityId is provided and we don't have org/team ID, infer from scope
    // But only for non-system roles (system roles have empty scopeEntityId)
    if (roleData.scopeEntityId && roleData.scope && !isSystem) {
      const scopeUpper = roleData.scope.toUpperCase();
      if (scopeUpper === 'ORGANIZATION' && !organizationId) {
        organizationId = roleData.scopeEntityId;
      } else if (scopeUpper === 'TEAM' && !teamId) {
        teamId = roleData.scopeEntityId;
      }
    }

    return {
      _id: roleData.id || roleData._id,
      id: roleData.id || roleData._id,
      name: roleData.name,
      description: roleData.description,
      permissions: roleData.permissions || [],
      level,
      organizationId,
      teamId,
      isSystem,
      isDefault: roleData.isDefault !== undefined ? roleData.isDefault : roleData.is_default,
      createdAt: roleData.createdAt || roleData.created_at,
      updatedAt: roleData.updatedAt || roleData.updated_at
    };
  }

  /**
   * Normalize member data from API response
   */
  private normalizeMember(memberData: any): Member {
    return {
      id: memberData.id || memberData._id,
      userId: memberData.userId || memberData.user_id,
      organizationId: memberData.organizationId || memberData.organization_id,
      teamId: memberData.teamId || memberData.team_id,
      roleId: memberData.roleId || memberData.role_id,
      roleName: memberData.roleName || memberData.role_name,
      permissions: memberData.permissions,
      joinedAt: memberData.joinedAt || memberData.joined_at
    };
  }
}

// Export singleton instance
export const roleService = new RoleService();